"use client"

import { useEffect, useRef, useState } from "react"
import AssetComboIcon from "../DefuseSDK/components/Asset/AssetComboIcon"
import ScrollingNumber from "./ScrollingNumber"

const DAY_DURATION_MS = 16000
const TICK_INTERVAL_MS = 2000
const ANNUAL_YIELD = 0.1
const DAILY_YIELD = ANNUAL_YIELD / 365
const TICK_YIELD = (1 + DAILY_YIELD) ** (TICK_INTERVAL_MS / DAY_DURATION_MS) - 1

const earnTokens = [
  {
    symbol: "USDC",
    icon: "/static/icons/token/usdc.svg",
    amount: 5000,
    price: 1,
    amountDigits: 1,
    valueDigits: 1,
  },
  {
    symbol: "NEAR",
    icon: "/static/icons/token/near.svg",
    amount: 2593,
    price: 1.85,
    amountDigits: 1,
    valueDigits: 1,
  },
  {
    symbol: "ETH",
    icon: "/static/icons/network/ethereum.svg",
    amount: 2.4,
    price: 1861.83,
    amountDigits: 4,
    valueDigits: 1,
  },
  {
    symbol: "SOL",
    icon: "/static/icons/network/solana.svg",
    amount: 62,
    price: 76.48,
    amountDigits: 3,
    valueDigits: 1,
  },
] as const

function EarnTokenRow({
  token,
  amount,
}: {
  token: (typeof earnTokens)[number]
  amount: number
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-gray-850 p-5">
      <AssetComboIcon icon={token.icon} sizeClassName="size-12" />
      <div className="flex flex-col">
        <div className="font-semibold text-base/5 text-white tabular-nums tracking-tighter">
          <ScrollingNumber value={amount} digits={token.amountDigits} />{" "}
          {token.symbol}
        </div>
        <div className="font-medium text-gray-400 text-sm/5 tabular-nums tracking-tighter">
          <ScrollingNumber
            value={amount * token.price}
            digits={token.valueDigits}
            prefix="$"
          />
        </div>
      </div>
    </div>
  )
}

const EarnIllustration = ({ isActive = true }: { isActive?: boolean }) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [amounts, setAmounts] = useState<number[]>(() =>
    earnTokens.map(({ amount }) => amount as number)
  )

  useEffect(() => {
    const element = rootRef.current
    if (!element) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true)
        }
      },
      { threshold: 0.4 }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isActive || !isInView) {
      return
    }

    const tick = () => {
      setAmounts((prev) => prev.map((amount) => amount * (1 + TICK_YIELD)))
    }

    let interval: ReturnType<typeof setInterval> | undefined
    const initialTimeout = setTimeout(() => {
      tick()
      interval = setInterval(tick, TICK_INTERVAL_MS)
    }, 500)

    return () => {
      clearTimeout(initialTimeout)
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [isActive, isInView])

  return (
    <div
      ref={rootRef}
      className="w-full max-w-[min(85vw,26rem)] space-y-1 rounded-2xl border border-white/5 bg-gray-900 p-1"
    >
      {earnTokens.map((token, index) => (
        <EarnTokenRow
          key={token.symbol}
          token={token}
          amount={amounts[index] ?? (token.amount as number)}
        />
      ))}
    </div>
  )
}

export default EarnIllustration
